import React from 'react';
import Label from '@/dashboard/components/Form/components/Label';
import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { confirm } from '@dashboard/lib/confirm';

/**
 * LabelToggle - A reusable component that conditionally applies styling based on an enabled state
 * Useful for graying out form sections when they are disabled
 */
const LabelToggle = ({
  label,
  enabled,
  canToggle,
  toggle,
  canReset,
  reset,
  resetToDefaults,
  resetTier,
  isAtDefault,
  size = 'sm',
}) => {
  return (
    <div className="relative">
      <div className="flex items-center gap-x-2 relative">
        <div className="max-w-2xs flex-1 flex items-center gap-x-2">
          <Label
            className={clsx('truncate !mb-0', {
              '!text-sm': size === 'sm',
              '!text-sm': size === 'md',
              'opacity-50': !enabled,
            })}
          >
            {label}
          </Label>
        </div>
      </div>

      {(canToggle || canReset) && (
        <div className="flex items-center gap-1 absolute top-0 right-0 z-0 pointer-events-auto">
          {canToggle && (
            <button
              type="button"
              onClick={toggle}
              className={`p-1 h-6 w-6 rounded flex items-center justify-center`}
              title={enabled ? `Hide ${label}` : `Show ${label}`}
            >
              {enabled ? (
                <EyeIcon className="h-3 w-3 transition-colors text-gray-500 dark:text-muted-foreground hover:text-gray-600 dark:hover:text-muted-foreground" />
              ) : (
                <EyeSlashIcon className="h-3 w-3 transition-colors text-gray-500 dark:text-muted-foreground hover:text-gray-600 dark:hover:text-muted-foreground" />
              )}
            </button>
          )}

          {canReset && (
            <button
              type="button"
              disabled={isAtDefault}
              onClick={async () => {
                if (resetTier === 2) {
                  const confirmed = await confirm('Remove theme value?', {
                    description:
                      'This will clear the value from your theme so it uses the system default instead.',
                    confirm: 'Clear value',
                    cancel: 'Cancel',
                  });
                  if (confirmed && resetToDefaults) resetToDefaults();
                } else {
                  reset();
                }
              }}
              className={`p-1 h-6 w-6 rounded flex items-center justify-center ${isAtDefault ? 'text-gray-300 cursor-default' : resetTier === 2 ? 'text-gray-400 hover:text-gray-600 dark:hover:text-muted-foreground cursor-pointer' : 'text-gray-500 hover:text-gray-700'}`}
              title={
                isAtDefault
                  ? 'Already at default'
                  : resetTier === 2
                    ? 'Clear to default'
                    : 'Reset to theme'
              }
            >
              <ArrowPathIcon className="h-3 w-3 transition-colors" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LabelToggle;
